class RoofBarModal extends HTMLElement {
  constructor () {
    super()
    this.initialize()
  }

  initialize () {
    this.pendingCartResponse = null
    this.cartDrawerShouldOpen = false
    this.originalRenderContents = null
    this.programmaticClose = false
    this.roofBarModalOpen = false

    // Wait for cart drawer to be ready then intercept
    this.waitForCartDrawerAndIntercept()

    // Listen for cart updates
    window.addEventListener('cart:updated', this.handleCartUpdated.bind(this))

    // Listen for modal close events - use hide:page-obscure instead of close:modal
    window.addEventListener('hide:page-obscure', this.handleModalClosed.bind(this))

    this.previousCartItems = this.getCurrentCartItemsFromResponse(null)
  }

  waitForCartDrawerAndIntercept () {
    const cartDrawer = document.querySelector('cart-drawer')
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      this.interceptCartDrawerRenderContents()
    } else {
      // Wait a bit and try again - either cart drawer doesn't exist or method isn't ready
      setTimeout(() => this.waitForCartDrawerAndIntercept(), 100)
    }
  }

  interceptCartDrawerRenderContents () {
    const cartDrawer = document.querySelector('cart-drawer')
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
      return
    }

    // Store original renderContents method
    this.originalRenderContents = cartDrawer.renderContents.bind(cartDrawer)

    // Override renderContents to check for roof bar modal conditions
    cartDrawer.renderContents = (parsedState) => {
      const currentCartItems = this.getCurrentCartItemsFromResponse(parsedState)
      const newRoofBarItems = this.getNewRoofBarItems(this.previousCartItems, currentCartItems)

      if (newRoofBarItems.length > 0) {
        const hasRoofTent = this.cartHasRoofTent(currentCartItems)

        if (!hasRoofTent) {
          // Store the cart response and show modal instead
          this.pendingCartResponse = parsedState
          this.cartDrawerShouldOpen = true
          this.previousCartItems = currentCartItems

          // Update cart UI elements (like cart icon) without opening drawer
          this.updateCartUIOnly(parsedState)

          // Show modal immediately instead of cart drawer
          this.showRoofBarModal()
          return // Don't call original renderContents
        }
      }

      // Normal case - call original renderContents
      this.previousCartItems = currentCartItems
      this.originalRenderContents(parsedState)
    }
  }

  updateCartUIOnly (parsedState) {
    // Update cart icon and other UI elements without opening the drawer
    const cartIconSection = parsedState.sections && parsedState.sections['cart-icon-bubble']
    if (cartIconSection) {
      const cartIconElement = document.getElementById('cart-icon-bubble')
      if (cartIconElement) {
        cartIconElement.innerHTML = this.getSectionInnerHTML(cartIconSection, '.shopify-section')
      }
    }

    // Update cart drawer content so it's ready when manually opened
    const cartDrawerSection = parsedState.sections && parsedState.sections['cart-drawer']
    if (cartDrawerSection) {
      const cartDrawerElement = document.getElementById('CartDrawer')
      if (cartDrawerElement) {
        const drawerInner = cartDrawerElement.querySelector('.drawer__inner')
        if (drawerInner) {
          drawerInner.innerHTML = this.getSectionInnerHTML(cartDrawerSection, '.drawer__inner')
        }
      }
    }
  }

  getSectionInnerHTML (html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  handleCartUpdated (event) {
    if (!event.detail) return
    // Just update previous cart items for tracking
    this.previousCartItems = this.getCurrentCartItemsFromResponse(event.detail)
  }

  handleModalClosed () {
    // Only handle if our roof bar modal was open
    if (!this.roofBarModalOpen) {
      return
    }

    // Mark modal as closed
    this.roofBarModalOpen = false

    // If this is a programmatic close from cart drawer opening, ignore it
    if (this.programmaticClose) {
      this.programmaticClose = false
      return
    }

    // If user closed modal without clicking buttons, show cart drawer
    if (this.cartDrawerShouldOpen && this.pendingCartResponse) {
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        // Set flag to prevent recursion when cart drawer closes modal
        this.programmaticClose = true

        setTimeout(() => {
          // Ensure cart drawer is not in empty state
          cartDrawer.classList.remove('is-empty')

          // Open the cart drawer
          cartDrawer.open()
        }, 500) // Slightly longer delay to ensure modal is fully closed
      }
    }

    // Reset flags
    this.cartDrawerShouldOpen = false
    this.pendingCartResponse = null
  }

  getCurrentCartItemsFromResponse (cartResponse) {
    // Handle cart drawer renderContents parsedState format
    if (cartResponse && cartResponse.sections && cartResponse.sections['cart-drawer']) {
      // Parse the cart drawer HTML to extract cart items
      const parser = new DOMParser()
      const doc = parser.parseFromString(cartResponse.sections['cart-drawer'], 'text/html')
      const cartItems = []

      // Look for cart items in the parsed HTML
      const itemElements = doc.querySelectorAll('.cart-item, .cart-drawer__item')
      itemElements.forEach(item => {
        const titleLink = item.querySelector('.cart-item__name, .cart-drawer__item-title')
        if (titleLink && titleLink.href) {
          const productHandle = this.getProductHandleFromUrl(titleLink.href)
          if (productHandle) {
            cartItems.push({ id: productHandle })
          }
        }
      })

      return cartItems
    }

    // Handle standard cart response format (from cart:updated events)
    if (cartResponse && cartResponse.items) {
      return cartResponse.items.map(item => ({
        id: this.getProductHandleFromUrl(item.url),
        variantId: item.variant_id,
        productId: item.product_id,
        quantity: item.quantity,
      }))
    }

    // Fallback to DOM parsing
    return this.getCurrentCartItemsFromDOM()
  }

  getCurrentCartItemsFromDOM () {
    try {
      if (window.cart && window.cart.items) {
        return window.cart.items.map(item => ({
          id: this.getProductHandleFromUrl(item.url),
          variantId: item.variant_id,
          productId: item.product_id,
          quantity: item.quantity,
        }))
      }
      const cartItems = []
      const cartDrawerItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item, #main-cart-items .cart-item')
      cartDrawerItems.forEach(item => {
        const productHandle = this.extractProductHandleFromCartItem(item)
        if (productHandle) {
          cartItems.push({ id: productHandle })
        }
      })
      return cartItems
    } catch (error) {
      console.warn('Could not get cart items:', error)
      return []
    }
  }

  getProductHandleFromUrl (url) {
    if (!url) return null
    const urlParts = url.split('/products/')
    if (urlParts.length > 1) {
      return urlParts[1].split('?')[0].split('/')[0]
    }
    return null
  }

  extractProductHandleFromCartItem (itemElement) {
    const titleLink = itemElement.querySelector('.cart-item__name, .cart-drawer__item-title')
    if (titleLink && titleLink.href) {
      return this.getProductHandleFromUrl(titleLink.href)
    }
    return null
  }

  getNewRoofBarItems (previousItems, currentItems) {
    const previousHandles = previousItems.map(item => item.id).filter(Boolean)
    const newItems = currentItems.filter(item => item.id && !previousHandles.includes(item.id))
    const roofBarItems = newItems.filter(item => this.isRoofBarProduct(item.id))
    return roofBarItems
  }

  isRoofBarProduct (productHandle) {
    if (window.TentBox && window.TentBox.roofBarsCollection) {
      const isRoofBar = window.TentBox.roofBarsCollection.includes(productHandle)
      return isRoofBar
    }
    return false
  }

  cartHasRoofTent (cartItems) {
    return cartItems.some(item => this.isRoofTentProduct(item.id))
  }

  isRoofTentProduct (productHandle) {
    if (window.TentBox && window.TentBox.roofTentsCollection) {
      return window.TentBox.roofTentsCollection.includes(productHandle)
    }
    return false
  }

  showRoofBarModal () {
    // Show modal immediately without delays
    this.roofBarModalOpen = true
    this.displayModal()
  }

  displayModal () {
    const modalContent = this.buildModalContent()
    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: 'clamp(320px, 100svw, 500px)',
        padding: 'none',
        modalClassAdd: ['w-full', 'pb-6'],
        modalClassRemove: ['max-w-[800px]', 'min-w-[300px]', 'w-auto', 'md:w-auto'],
        headerClassAdd: ['absolute', 'top-0', 'left-0', 'right-0', 'z-20'],
        headerClassRemove: ['border-b'],
        bodyClassAdd: ['relative', 'z-10', 'p-6'],
        closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-20'],
      },
    }))
    setTimeout(() => {
      const modal = document.querySelector('reusable-modal')
      const modalBody = modal?.querySelector('.reusable-modal__body')

      if (modalBody) {
        modalBody.innerHTML = modalContent
        this.attachModalEventListeners(modal)
      }
    }, 100)
  }

  buildModalContent () {
    const settings = window.TentBox.roofBarModal

    return `
      <div class="roof-bar-modal__content pt-6 md:pt-0 text-center">
        <div class="roof-bar-modal__message mt-8 mb-8 text-2xl font-semibold font-body normal-case">
          ${settings.title}
        </div>
        <div class="roof-bar-modal__actions flex flex-col gap-6 text-center">
          ${settings.shopButtonUrl
? `
            <a href="${settings.shopButtonUrl}" class="button button-primary w-full roof-bar-modal__shop-button">
              ${settings.shopButtonText}
            </a>
          `
: ''}
          ${settings.compatibilityUrl
? `
            <div class="flex justify-center mt-1">
              <a href="${settings.compatibilityUrl}" class="inline-flex items-center hover:underline underline-offset-4 font-medium text-orange roof-bar-modal__compatibility-link">
                <div>${settings.compatibilityText}</div>
                <span class="sr-only">Link to ${settings.compatibilityText}</span>
                <div class="h-3.5 w-3.5 ml-1 text-orange">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
              </a>
            </div>
          `
: ''}
        </div>
      </div>
    `
  }

  attachModalEventListeners (modal) {
    const shopButton = modal.querySelector('.roof-bar-modal__shop-button')
    if (shopButton) {
      shopButton.addEventListener('click', () => {
        // User clicked button, don't show cart drawer
        this.roofBarModalOpen = false
        this.cartDrawerShouldOpen = false
        this.pendingCartResponse = null
        window.dispatchEvent(new CustomEvent('close:modal'))
      })
    }
    const compatibilityLink = modal.querySelector('.roof-bar-modal__compatibility-link')
    if (compatibilityLink) {
      compatibilityLink.addEventListener('click', () => {
        // User clicked button, don't show cart drawer
        this.roofBarModalOpen = false
        this.cartDrawerShouldOpen = false
        this.pendingCartResponse = null
        window.dispatchEvent(new CustomEvent('close:modal'))
      })
    }
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('roof-bar-modal')) {
      customElements.define('roof-bar-modal', RoofBarModal)
    }
  })
} else {
  if (!customElements.get('roof-bar-modal')) {
    customElements.define('roof-bar-modal', RoofBarModal)
  }
}
